<template>
  <!-- Palette header: click-to-edit title + Share / Edit / Download actions.
       Shows skeletons while a generation is loading. Its own frame rather than
       a BaseCard: a lighter 1px edge on the page's own gray-950, with the
       actions on a raised gray-900 bar. -->
  <div class="overflow-hidden rounded-lg border border-gray-600 bg-gray-950 shadow">
    <div class="py-2">
      <div class="group relative w-full">
        <!-- Loading placeholder -->
        <div v-if="isLoading" class="m-2 flex justify-center">
          <div class="my-1 h-5 w-36 animate-pulse rounded bg-gray-700"></div>
        </div>
        <template v-else>
          <!-- The title is a button, so renaming works from the keyboard too;
               the field takes its place (and its focus) while editing. -->
          <h3 class="text-center text-lg font-medium leading-10 text-white" :class="{ invisible: editMode }">
            <button
              type="button"
              class="w-full cursor-text truncate px-4 py-1.5 transition-opacity duration-200 group-hover:opacity-50 focus-visible:-outline-offset-2"
              :aria-label="`Rename palette: ${title}`"
              @click="activateEditMode"
            >
              {{ title }}
            </button>
          </h3>
          <input
            ref="inputField"
            v-model="title"
            type="text"
            aria-label="Palette name"
            class="absolute inset-0 w-full rounded-lg border-0 bg-transparent py-1.5 text-center text-lg font-medium text-white caret-white placeholder-transparent focus:ring-0"
            :class="editMode ? 'visible' : 'invisible'"
            @blur="editMode = false"
            @keydown.enter="inputField?.blur()"
            @keydown.esc="inputField?.blur()"
          />
        </template>
      </div>
    </div>

    <div v-if="!isLoading" class="mx-2.5 mb-2.5 flex flex-wrap justify-center gap-1 rounded-md bg-gray-900 p-1.5 px-6 md:gap-4">
      <GhostIconButton @click="$emit('share')">
        <template #icon>
          <LinkIcon class="size-4" aria-hidden="true" />
        </template>
        Share Link
      </GhostIconButton>

      <GhostIconButton :aria-pressed="enableEdit" @click="$emit('toggle-edit')">
        <template #icon>
          <XMarkIcon v-if="enableEdit" class="size-4 text-red-500" aria-hidden="true" />
          <PencilIcon v-else class="size-4" aria-hidden="true" />
        </template>
        {{ enableEdit ? 'Cancel Edit' : 'Edit Colors' }}
      </GhostIconButton>

      <GhostIconButton aria-haspopup="dialog" :aria-expanded="downloadOpen" @click="$emit('open-download')">
        <template #icon>
          <ArrowDownTrayIcon class="size-4" aria-hidden="true" />
        </template>
        Download Palette
      </GhostIconButton>
    </div>

    <div v-if="isLoading" class="m-2 flex flex-wrap justify-center gap-8">
      <div v-for="n in 3" :key="n" class="my-1 h-5 w-24 animate-pulse rounded bg-gray-700"></div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick } from 'vue'
import { ArrowDownTrayIcon, LinkIcon, PencilIcon, XMarkIcon } from '@heroicons/vue/16/solid'
import GhostIconButton from '~/components/atoms/GhostIconButton.vue'

const props = defineProps({
  modelValue: { type: String, default: '' },
  isLoading: { type: Boolean, default: false },
  enableEdit: { type: Boolean, default: false },
  downloadOpen: { type: Boolean, default: false },
})
const emit = defineEmits(['update:modelValue', 'share', 'toggle-edit', 'open-download'])

const editMode = ref(false)
const inputField = ref(null)

// Writable title bridged to the parent via v-model.
const title = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value),
})

// Activate edit mode and move caret to the end.
const activateEditMode = () => {
  editMode.value = true
  nextTick(() => {
    if (inputField.value) {
      const length = inputField.value.value.length
      inputField.value.focus()
      inputField.value.setSelectionRange(length, length)
    }
  })
}

watch(editMode, (newVal) => {
  if (newVal) {
    nextTick(() => inputField.value?.focus())
  }
})
</script>
